import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ShieldCheck, User } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ResponsiveDialog as Dialog,
  ResponsiveDialogContent as DialogContent,
  ResponsiveDialogFooter as DialogFooter,
  ResponsiveDialogHeader as DialogHeader,
  ResponsiveDialogTitle as DialogTitle,
  ResponsiveDialogDescription as DialogDescription,
  ResponsiveDialogBody as DialogBody,
} from "@/components/ui/responsive-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { formatDate, initials } from "@/lib/format";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

const EMPTY = { name: "", email: "", password: "", role: "tekniker" };

const RoleBadge = ({ role }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
      role === "admin" ? "border-transparent bg-[#0B0D10] text-white dark:bg-white dark:text-[#0B0D10]" : "bg-card text-foreground"
    )}
  >
    {role === "admin" ? <ShieldCheck className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
    {role === "admin" ? "Administrator" : "Tekniker"}
  </span>
);

export default function Brukarar() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Namn er påkrevd.");
    if (!editing && (!form.email.trim() || !form.password)) return toast.error("E-post og passord er påkrevd.");
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success(editing ? "Brukar oppdatert." : "Brukar oppretta.");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Noko gjekk gale.");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error("Kunne ikkje endre status."); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Brukar sletta.");
      setToDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikkje slette."); setToDelete(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Administrer teknikarar og administratorar.</p>
        <Button onClick={openNew} data-testid="new-user-button" className="shrink-0">
          <Plus /> Ny brukar
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}</div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-2 md:hidden" data-testid="user-card-list">
            {users.map((u) => (
              <Card key={u.id} className="p-3 shadow-sm" data-testid={`user-card-${u.id}`}>
                <div className="flex items-start gap-3">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#0B0D10] text-xs font-bold text-white dark:bg-white dark:text-[#0B0D10]">{initials(u.name)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-semibold">{u.name}</span>
                      {u.id === me?.id && <span className="rounded-full bg-primary/15 px-1.5 text-[10px] font-semibold uppercase tracking-wider">Deg</span>}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <RoleBadge role={u.role} />
                      <span className="text-[11px] text-muted-foreground">Oppretta {formatDate(u.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between border-t pt-3">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={u.id === me?.id} data-testid={`user-active-${u.id}`} />
                    <span className={u.active ? "font-medium" : "text-muted-foreground"}>{u.active ? "Aktiv" : "Deaktivert"}</span>
                  </label>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} aria-label="Rediger"><Pencil className="h-4 w-4" /></Button>
                    {u.id !== me?.id && (
                      <Button size="icon" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(u)} data-testid={`delete-user-${u.id}`} aria-label="Slett"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Desktop table */}
          <Card className="hidden overflow-hidden shadow-sm md:block">
            <div className="overflow-x-auto">
              <Table data-testid="user-list-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>E-post</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Oppretta</TableHead>
                    <TableHead>Aktiv</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell><RoleBadge role={u.role} /></TableCell>
                      <TableCell className="text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                      <TableCell>
                        <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={u.id === me?.id} />
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button size="iconSm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} aria-label="Rediger"><Pencil className="h-4 w-4" /></Button>
                          {u.id !== me?.id && (
                            <Button size="iconSm" variant="ghost" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setToDelete(u)} aria-label="Slett"><Trash2 className="h-4 w-4" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Rediger brukar" : "Ny brukar"}</DialogTitle>
            <DialogDescription>{editing ? "Endre namn, rolle eller passord." : "Legg til ein ny brukar med tilgang til systemet."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="contents" data-testid="user-form">
            <DialogBody>
              <div className="space-y-1.5">
                <Label>Namn *</Label>
                <Input className="h-11" data-testid="user-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>E-post *</Label>
                <Input className="h-11" type="email" inputMode="email" data-testid="user-email-input" value={form.email} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>{editing ? "Nytt passord (valfritt)" : "Passord *"}</Label>
                <Input className="h-11" type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder={editing ? "La stå tom for å behalde" : ""} />
              </div>
              <div className="space-y-1.5">
                <Label>Rolle</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger className="h-11" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tekniker">Tekniker</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="user-cancel-button">Avbryt</Button>
              <Button type="submit" disabled={busy} data-testid="save-user-button">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Lagre
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Slette brukar?</AlertDialogTitle>
            <AlertDialogDescription>{toDelete?.name} mistar tilgangen til systemet.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-user">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
